import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Camera, CreditCard, Search, Lock, Fingerprint, Cpu, Wrench } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1746005718013-b24074afb701?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxzbWFydHBob25lJTIwcmVwYWlyJTIwY2xvc2V1cHxlbnwwfHx8fDE3ODYyNzM2MDh8MA&ixlib=rb-4.1.0&q=85";
const TRUST = "https://images.unsplash.com/photo-1550041473-d296a3a8a18a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxtb2JpbGUlMjByZXBhaXIlMjB0ZWNobmljaWFufGVufDB8fHx8MTc4NjI3MzYwOHww&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  const [code, setCode] = useState("");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (!c) return;
    nav(`/rastrear/${c.startsWith("OS-") ? c : `OS-${c}`}`);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-8">
              <span className="w-8 h-px bg-neutral-900" /> Assistência Técnica Digital
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight text-neutral-900">
              Seu reparo,<br/>
              <span className="italic font-light">transparente</span> <br/>
              do começo ao fim.
            </h1>
            <p className="mt-8 text-base sm:text-lg text-neutral-600 max-w-lg leading-relaxed">
              Acompanhe o status do seu aparelho em tempo real, veja fotos do técnico e pague online com segurança —
              tudo com o código da sua Ordem de Serviço.
            </p>

            <div className="mt-10 max-w-2xl">
              <Button
                type="button"
                onClick={() => nav("/solicitar-reparo")}
                data-testid="request-repair-btn"
                className="w-full h-16 rounded-md bg-lime-400 hover:bg-lime-300 text-neutral-900 text-lg font-black tracking-tight shadow-lg shadow-lime-500/30 border-2 border-neutral-900"
              >
                <Wrench size={20} className="mr-2.5"/> Solicitar Reparo <ArrowRight className="ml-2" size={20}/>
              </Button>
              <div className="mt-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-neutral-300"/>
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500">ou</span>
                <div className="h-px flex-1 bg-neutral-300"/>
              </div>
            </div>

            <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl" data-testid="track-form">
              <div className="relative flex-1">
                <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  data-testid="track-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="OS-12345"
                  className="h-16 pl-12 text-2xl font-mono font-bold uppercase bg-white border-neutral-300 focus:border-neutral-900 focus:ring-neutral-900"
                />
              </div>
              <Button type="submit" data-testid="track-submit" className="h-16 px-8 rounded-md bg-neutral-900 hover:bg-neutral-800 text-white text-base font-semibold">
                Verificar OS <ArrowRight className="ml-2" size={18}/>
              </Button>
            </form>
            <p className="mt-3 text-xs text-neutral-500 max-w-2xl">Sem cadastro. Digite o código de acompanhamento entregue na loja.</p>
          </div>

          {/* Right hero art */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl">
              <img src={HERO} alt="Reparo de smartphone" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-xl p-4 border border-white/60">
                <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Status atual</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="status-badge status-concluido">● Serviço Concluído</span>
                </div>
                <div className="font-mono text-sm mt-2 text-neutral-800">OS-84291 · iPhone 13 Pro</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-neutral-900 text-white p-5 rounded-2xl shadow-xl hidden md:block">
              <div className="text-3xl font-heading font-black">12 min</div>
              <div className="text-xs text-neutral-400">tempo médio de resposta</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="como-funciona" className="bg-white border-y border-neutral-200 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500">Fluxo transparente</div>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-heading font-black tracking-tight">Três estados. Zero surpresas.</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {[
              { t: "Orçamento", s: "01", d: "Cadastramos seu aparelho e diagnosticamos o problema.", cls: "status-orcamento" },
              { t: "Aguardando Autorização", s: "02", d: "Você aprova o orçamento antes de qualquer reparo.", cls: "status-aguardando" },
              { t: "Serviço Concluído", s: "03", d: "Pague online (Pix/Cartão) ou na retirada do aparelho.", cls: "status-concluido" },
            ].map((x) => (
              <div key={x.s} className="border border-neutral-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all bg-neutral-50/50">
                <div className="font-mono text-xs text-neutral-400">{x.s}</div>
                <div className={`status-badge ${x.cls} mt-3`}>{x.t}</div>
                <p className="mt-6 text-neutral-700 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section id="seguranca" className="py-16 sm:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl overflow-hidden aspect-[5/4]">
              <img src={TRUST} alt="Técnico especialista" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500">Segurança & LGPD</div>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-heading font-black tracking-tight">Seus dados, protegidos por lei.</h2>
            <div className="mt-10 space-y-6">
              {[
                { icon: Lock, t: "Criptografia AES-256", d: "Todos os dados sensíveis armazenados com criptografia de nível bancário." },
                { icon: Fingerprint, t: "Mascaramento na consulta pública", d: "CPF e telefone parcialmente ocultos (ex: 123.***.***-45) na busca sem login." },
                { icon: ShieldCheck, t: "PCI-DSS Compliance", d: "Dados de cartão nunca tocam nossos servidores — processados pelo gateway." },
              ].map((f) => (
                <div key={f.t} className="flex gap-4">
                  <div className="w-11 h-11 bg-neutral-900 text-white rounded-lg grid place-items-center shrink-0">
                    <f.icon size={18} />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-lg">{f.t}</div>
                    <div className="text-neutral-600 text-sm mt-1 leading-relaxed">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="font-heading font-black">GTec ©</div>
          <div className="text-xs text-neutral-500">Plataforma de gestão de assistência técnica · LGPD-ready</div>
        </div>
      </footer>
    </div>
  );
}
